/**
 * "Email Creation" ticket category: one ticket can carry many employees whose
 * mailboxes are requested. This file is the single source of truth for the
 * row shape and its validation, used by BOTH the web form (instant feedback)
 * and the API (`createTicketDtoSchema`, the authoritative check).
 *
 * Personal data: values here are employee PII. Nothing in the application may
 * log them; audit rows record counts only.
 */
import { z } from 'zod';
/** Stable catalogue code of the category (names can be renamed by an admin; codes are not). */
export const EMAIL_CREATION_CATEGORY_CODE = 'EMAIL_CREATION';
/**
 * Safety cap per ticket. The spec asks for no practical limit; this exists only
 * so a single request body cannot be used to insert an unbounded number of rows.
 */
export const EMAIL_CREATION_MAX_USERS = 100;
export const EMAIL_CREATION_REQUEST_TYPES = ['NEW', 'REPLACEMENT'];
export const EMAIL_CREATION_REQUEST_TYPE_LABELS = {
    NEW: 'New',
    REPLACEMENT: 'Replacement',
};
/** Employee ID values that are deliberately shared (e.g. generic/shared mailboxes) and so are exempt from the duplicate check. */
const SHARED_EMPLOYEE_ID = 'GENERIC ID';
export const EMAIL_CREATION_FIELDS = [
    'firstName',
    'lastName',
    'emailId',
    'requestType',
    'employeeId',
    'mobileNumber',
    'designation',
    'department',
    'city',
    'officeLocationId',
    'companyName',
    'reportingManager',
];
export const EMAIL_CREATION_FIELD_LABELS = {
    firstName: 'First Name',
    lastName: 'Last Name',
    emailId: 'Email ID',
    requestType: 'New / Replacement',
    employeeId: 'Employee ID / Number',
    mobileNumber: 'Mobile Number',
    designation: 'Title / Designation',
    department: 'Department',
    city: 'City',
    officeLocationId: 'Office Location',
    companyName: 'Company Name',
    reportingManager: 'Reporting Manager',
};
const MAX_LEN = {
    firstName: 100,
    lastName: 100,
    emailId: 254,
    employeeId: 50,
    mobileNumber: 25,
    designation: 150,
    department: 100,
    city: 100,
    officeLocationId: 36,
    companyName: 200,
};
export const EMAIL_CREATION_MAX_LENGTHS = MAX_LEN;
/** Deliberately permissive: no shape is imposed beyond "looks like an address". */
const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/;
/** Letters, digits and common separators. Not numeric-only: "E11515" and "GENERIC ID" are both valid. */
const EMPLOYEE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9 ._\-/]*$/;
/** Optional leading +, digits with spaces, dashes or brackets; 7-15 digits (E.164 maximum), so international numbers pass. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MOBILE_RE = /^\+?[0-9][0-9 ()-]*$/;
/**
 * Reporting manager reference, picked from the people search (never typed):
 * `entra:<object id>` for someone found in Entra ID, `user:<helpdesk user id>`
 * for someone who already has a helpdesk account. The server re-reads the
 * person from Entra / the users table and snapshots name + email itself; it
 * never trusts a name sent by the browser.
 */
const MANAGER_REF_RE = /^(entra|user):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
export function parseReportingManagerRef(ref) {
    const m = MANAGER_REF_RE.exec((ref ?? '').trim());
    return m ? { kind: m[1].toLowerCase(), id: m[2].toLowerCase() } : null;
}
export function reportingManagerRef(kind, id) {
    return `${kind}:${id.toLowerCase()}`;
}
/** Per-row field validation. Returns `{}` when the row is valid. Messages are written for end users. */
export function validateEmailCreationUser(u) {
    const errors = {};
    const val = (s) => (s ?? '').trim();
    for (const f of ['firstName', 'lastName', 'designation', 'department', 'city', 'companyName']) {
        const v = val(u[f]);
        const label = EMAIL_CREATION_FIELD_LABELS[f];
        if (!v)
            errors[f] = `${label} is required.`;
        else if (v.length > MAX_LEN[f])
            errors[f] = `${label} must be ${MAX_LEN[f]} characters or fewer.`;
    }
    if (!val(u.officeLocationId))
        errors.officeLocationId = 'Select an office location.';
    else if (!UUID_RE.test(val(u.officeLocationId)))
        errors.officeLocationId = 'Select an office location from the list.';
    if (!EMAIL_CREATION_REQUEST_TYPES.includes(u.requestType)) {
        errors.requestType = 'Choose New or Replacement.';
    }
    const email = val(u.emailId);
    if (email) {
        if (email.length > MAX_LEN.emailId || !EMAIL_RE.test(email))
            errors.emailId = 'Enter a valid email address, for example name@company.com.';
    }
    // Email ID is the ONLY optional field, for New and Replacement alike.
    if (!val(u.reportingManager))
        errors.reportingManager = 'Select the reporting manager.';
    else if (!parseReportingManagerRef(u.reportingManager))
        errors.reportingManager = 'Search for the reporting manager and pick them from the list.';
    const emp = val(u.employeeId);
    if (!emp)
        errors.employeeId = 'Employee ID / Number is required.';
    else if (emp.length > MAX_LEN.employeeId)
        errors.employeeId = `Employee ID / Number must be ${MAX_LEN.employeeId} characters or fewer.`;
    else if (!EMPLOYEE_ID_RE.test(emp))
        errors.employeeId = 'Use letters, numbers, spaces, dots, dashes or slashes only.';
    const mob = val(u.mobileNumber);
    if (!mob)
        errors.mobileNumber = 'Mobile Number is required.';
    else {
        const digits = mob.replace(/\D/g, '').length;
        if (mob.length > MAX_LEN.mobileNumber || !MOBILE_RE.test(mob) || digits < 7 || digits > 15) {
            errors.mobileNumber = 'Enter a valid mobile number (7 to 15 digits; a leading + is allowed).';
        }
    }
    return errors;
}
export const EMAIL_CREATION_DUPLICATE_MESSAGE = 'This employee appears to have been added more than once.';
/** Obvious duplicates: the same email address, or the same employee ID (case-insensitive; the shared "GENERIC ID" is exempt). */
export function findEmailCreationDuplicates(rows) {
    const out = [];
    const seenEmp = new Map();
    const seenEmail = new Map();
    rows.forEach((r, index) => {
        const emp = (r.employeeId ?? '').trim().toUpperCase();
        if (emp && emp !== SHARED_EMPLOYEE_ID) {
            const prev = seenEmp.get(emp);
            if (prev === undefined)
                seenEmp.set(emp, index);
            else
                out.push({ index, field: 'employeeId', duplicateOf: prev });
        }
        const email = (r.emailId ?? '').trim().toLowerCase();
        if (email) {
            const prev = seenEmail.get(email);
            if (prev === undefined)
                seenEmail.set(email, index);
            else
                out.push({ index, field: 'emailId', duplicateOf: prev });
        }
    });
    return out;
}
const trimmed = (max) => z.string().trim().max(max);
/** One requested employee as accepted by `POST /tickets`. Trimmed; field rules come from `validateEmailCreationUser`. */
export const emailCreationUserSchema = z
    .object({
    firstName: trimmed(MAX_LEN.firstName),
    lastName: trimmed(MAX_LEN.lastName),
    emailId: z.string().trim().max(MAX_LEN.emailId).nullish(),
    requestType: z.string(),
    employeeId: trimmed(MAX_LEN.employeeId),
    mobileNumber: trimmed(MAX_LEN.mobileNumber),
    designation: trimmed(MAX_LEN.designation),
    department: trimmed(MAX_LEN.department),
    city: trimmed(MAX_LEN.city),
    officeLocationId: trimmed(MAX_LEN.officeLocationId),
    companyName: trimmed(MAX_LEN.companyName),
    reportingManager: z.string().trim().max(80),
})
    .strict()
    .superRefine((u, ctx) => {
    for (const [field, message] of Object.entries(validateEmailCreationUser(u))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
    }
})
    .transform((u) => ({ ...u, emailId: u.emailId && u.emailId.length > 0 ? u.emailId : null, requestType: u.requestType }));
/** The list on `POST /tickets`: at least one user (when the category requires it), no obvious duplicates, bounded size. */
export const emailCreationUsersSchema = z
    .array(emailCreationUserSchema)
    .min(1)
    .max(EMAIL_CREATION_MAX_USERS)
    .superRefine((rows, ctx) => {
    for (const d of findEmailCreationDuplicates(rows)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [d.index, d.field], message: EMAIL_CREATION_DUPLICATE_MESSAGE });
    }
});
/** A stored row as returned on ticket detail. */
export const emailCreationUserRecordSchema = z.object({
    id: z.string().uuid(),
    position: z.number().int().positive(),
    firstName: z.string(),
    lastName: z.string(),
    emailId: z.string().nullable(),
    requestType: z.enum(EMAIL_CREATION_REQUEST_TYPES),
    employeeId: z.string(),
    mobileNumber: z.string(),
    designation: z.string(),
    department: z.string(),
    city: z.string(),
    /** Snapshot of the office-location CODE at submission; stays correct if the location is later renamed or disabled. */
    officeLocation: z.string(),
    officeLocationId: z.string().uuid().nullable(),
    companyName: z.string(),
    /** Snapshot taken at submission (null on requests raised before the field existed). */
    reportingManagerName: z.string().nullable(),
    reportingManagerEmail: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// People search for the Reporting Manager field (`GET /directory/people?q=`)
// ---------------------------------------------------------------------------
export const PEOPLE_SEARCH_MIN = 2;
export const PEOPLE_SEARCH_MAX = 100;
export const PEOPLE_SEARCH_LIMIT = 10;
export const peopleSearchQueryDtoSchema = z
    .object({
    q: z
        .string()
        .transform((v) => v.trim())
        .pipe(z.string().min(PEOPLE_SEARCH_MIN, 'Type at least 2 characters.').max(PEOPLE_SEARCH_MAX)),
})
    .strict();
//# sourceMappingURL=email-creation.js.map